import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import ModalComponent from "../../components/modalComponent";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import ButtonComponent from "../../components/button";
import { Grid, Tabs, Tab, Box } from "@mui/material";
import TableComponent from "../../components/tableComponent";
import { actionsColumn, textColumn } from "../../components/tableComponent/columns";
import StatusPill from "../../components/statusPill";
import { user_license_status_select } from "../../utils/selectsValues";
import SelectFeatureModal from "../pricingInstance/selectFeaturesModal";
import icons from "../../constants/icons";
import { callApiAsync, createUpdatePricingInstance, getCurrency, getUsersByBusinessUnit, getEditPricingInstanceData, getUsersForNewPricingInstance } from "../../services/apiService";
import MoneyTextInput from "../../components/textInput/moneyTexInput";
import TextInput from "../../components/textInput";
import SelectInput from "../../components/selectInput";
import DatePickerInput from "../../components/datepickerInput";
import FormRadioButton from "../../components/formRadio";
import dayjs from "dayjs";

const licenseLabel = (status) =>
  user_license_status_select.find((o) => o.value === status)?.label ?? "Activa";

const licensePillColor = (status) => {
  if (status === 1) return "#4ADE80";
  if (status === 2) return "#FCA5A5";
  return "#D9D9D9";
};

const TIPO_FACTURACION = {
  individual: "individual",
  precio_paquete: "precio_paquete",
  tamano_paquete: "tamano_paquete",
  max_miembros: "max_miembros"
};

const TIPO_FACTURACION_OPTIONS = [
  { value: TIPO_FACTURACION.individual, label: "Precio individual" },
  { value: TIPO_FACTURACION.precio_paquete, label: "Precio del paquete" },
  { value: TIPO_FACTURACION.tamano_paquete, label: "Tamaño de paquete" },
  { value: TIPO_FACTURACION.max_miembros, label: "Máximo de miembros" }
];

/** Valor del enum Currency en backend: 0 = COP, 1 = USD. COP por defecto. */
const CURRENCY_COP = "0";
const CURRENCY_USD = "1";

function transformCurrencyData(currencyData) {
  return (currencyData || []).map((item) => ({
    value: item.id?.toString() ?? String(item.Id),
    label: (item.siglas || item.Siglas || "").trim()
  }));
}

function parsePrice(val) {
  if (val === "" || val == null) return 0;
  if (typeof val === "number" && !Number.isNaN(val)) return val;
  const str = String(val).replace(/,/g, "");
  const num = parseFloat(str);
  return Number.isNaN(num) ? 0 : num;
}

function lastDayOfCurrentMonth() {
  return dayjs().endOf("month");
}

const NOT_BOTH_TEMPORAL_MSG =
  "Solo puede ser adición temporal o remoción temporal, no ambos.";

function getSchema(tipoFacturacion, temporalAddition, temporalRemoval) {
  const isTemporal = temporalAddition === "1" || temporalRemoval === "1";
  const base = {
    tipoFacturacion: yup.string(),
    individualPrice: yup.string(),
    packageSize: yup.string(),
    packagePrice: yup.string(),
    maxMembersToCharge: yup.string(),
    tax: yup.string().required("El impuesto es requerido"),
    currencyId: yup.string(),
    dueDate: isTemporal
      ? yup.mixed().required("La fecha de vencimiento es obligatoria cuando hay adición o remoción temporal")
      : yup.mixed().nullable(),
    temporalAddition: yup.string(),
    temporalRemoval: yup.string()
  };
  const notBothTemporal = yup.object().test(
    "not-both-temporal",
    NOT_BOTH_TEMPORAL_MSG,
    (obj) => !(obj?.temporalAddition === "1" && obj?.temporalRemoval === "1")
  );
  let schema;
  switch (tipoFacturacion) {
    case TIPO_FACTURACION.individual:
      schema = yup.object().shape({
        ...base,
        individualPrice: yup.string().required("El precio individual es requerido")
      });
      break;
    case TIPO_FACTURACION.precio_paquete:
      schema = yup.object().shape({
        ...base,
        packagePrice: yup.string().required("El precio del paquete es requerido")
      });
      break;
    case TIPO_FACTURACION.tamano_paquete:
      schema = yup.object().shape({
        ...base,
        packageSize: yup.string().required("El tamaño del paquete es requerido"),
        packagePrice: yup.string().required("El precio del paquete es requerido")
      });
      break;
    case TIPO_FACTURACION.max_miembros:
      schema = yup.object().shape({
        ...base,
        packagePrice: yup.string().required("El precio del paquete es requerido"),
        maxMembersToCharge: yup.string().required("Máximo de miembros a cobrar es requerido")
      });
      break;
    default:
      schema = yup.object().shape(base);
  }
  return schema.concat(notBothTemporal);
}

function inferTipoFacturacion(data) {
  const max = data?.maxMembersToCharge ?? data?.MaxMembersToCharge ?? 0;
  const pSize = data?.packageSize ?? data?.PackageSize ?? 0;
  const pPrice = data?.packagePrice ?? data?.PackagePrice ?? 0;
  if (max > 0) return TIPO_FACTURACION.max_miembros;
  if (pSize > 0 && pPrice > 0) return TIPO_FACTURACION.tamano_paquete;
  if (pPrice > 0) return TIPO_FACTURACION.precio_paquete;
  return TIPO_FACTURACION.individual;
}

const AddPackageToBillModal = ({
  open,
  onClose,
  packageRow,
  idBillingPeriod,
  pricingInstanceId,
  onSuccess,
  onError
}) => {
  const userData = useSelector((state) => state.user?.userData);
  const isEditMode = Boolean(pricingInstanceId);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [editInstance, setEditInstance] = useState(null);
  const [canCreateTemporal, setCanCreateTemporal] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);

  const { control, handleSubmit, reset, watch, setValue, getValues } = useForm({
    resolver: (values, context, options) => {
      const tipo = values.tipoFacturacion || TIPO_FACTURACION.individual;
      const temporalAdd = values.temporalAddition || "0";
      const temporalRem = values.temporalRemoval || "0";
      return yupResolver(getSchema(tipo, temporalAdd, temporalRem))(values, context, options);
    },
    defaultValues: {
      tipoFacturacion: TIPO_FACTURACION.individual,
      individualPrice: "0",
      packageSize: "",
      packagePrice: "0",
      maxMembersToCharge: "",
      tax: "0",
      currencyId: CURRENCY_COP,
      temporalAddition: "0",
      temporalRemoval: "0",
      dueDate: null
    }
  });

  const tipoFacturacion = watch("tipoFacturacion") || TIPO_FACTURACION.individual;
  const temporalAddition = watch("temporalAddition") || "0";
  const temporalRemoval = watch("temporalRemoval") || "0";

  const showIndividualPrice = tipoFacturacion === TIPO_FACTURACION.individual;
  const showPackageSize = tipoFacturacion === TIPO_FACTURACION.tamano_paquete;
  const showPackagePrice =
    tipoFacturacion === TIPO_FACTURACION.precio_paquete ||
    tipoFacturacion === TIPO_FACTURACION.tamano_paquete ||
    tipoFacturacion === TIPO_FACTURACION.max_miembros;
  const showMaxMembers = tipoFacturacion === TIPO_FACTURACION.max_miembros;

  useEffect(() => {
    callApiAsync(getCurrency, (data) => {
      setCurrencyOptions(transformCurrencyData(data));
    });
  }, []);

  const idBusinessUnit = packageRow?.idBusinessUnit ?? packageRow?.IdBusinessUnit;

  useEffect(() => {
    if (!open) {
      setEditInstance(null);
      setSelectedFeatures([]);
      setActiveTab(0);
      return;
    }
    if (pricingInstanceId) {
      callApiAsync(
        () => getEditPricingInstanceData(pricingInstanceId),
        (data) => {
          setEditInstance(data);
          setAllUsers(data?.allUsers ?? []);
          setSelectedUserIds(data?.selectedUserIds ?? []);
          const featuresArray = Array.isArray(data?.selectedFeatures)
            ? data.selectedFeatures
            : [];
          setSelectedFeatures(featuresArray);
          const isAdd = data?.temporalAddition ?? data?.TemporalAddition;
          const isRem = data?.temporalRemoval ?? data?.TemporalRemoval;
          const canAdd = data?.canCreateAddition !== false;
          const canRem = data?.canCreateRemoval !== false;
          setCanCreateTemporal((isAdd && !canAdd) || (isRem && !canRem) ? false : true);
          const d = data;
          const currencyId = String(d?.currencyId ?? d?.CurrencyId ?? 0);
          const tax = (d?.tax ?? d?.Tax ?? 0);
          const temporalAdd = (d?.temporalAddition ?? d?.TemporalAddition) ? "1" : "0";
          const temporalRem = (d?.temporalRemoval ?? d?.TemporalRemoval) ? "1" : "0";
          const due = d?.dueDate ?? d?.DueDate;
          reset({
            tipoFacturacion: inferTipoFacturacion(d),
            individualPrice: String(d?.individualPrice ?? d?.IndividualPrice ?? 0),
            packageSize: String(d?.packageSize ?? d?.PackageSize ?? ""),
            packagePrice: String(d?.packagePrice ?? d?.PackagePrice ?? 0),
            maxMembersToCharge: String(d?.maxMembersToCharge ?? d?.MaxMembersToCharge ?? ""),
            tax: String(tax),
            currencyId: currencyId,
            temporalAddition: temporalAdd,
            temporalRemoval: temporalRem,
            dueDate: due ? dayjs(due) : null
          });
        },
        (err) => onError?.(err?.message ?? err?.error ?? "Error al cargar datos")
      );
      return;
    }
    const defaultCurrency =
      currencyOptions.length > 0
        ? (currencyOptions.find((o) => o.label === "COP")?.value ?? currencyOptions[0].value)
        : CURRENCY_COP;
    reset({
      tipoFacturacion: TIPO_FACTURACION.individual,
      individualPrice: "0",
      packageSize: "",
      packagePrice: "0",
      maxMembersToCharge: "",
      tax: "0",
      currencyId: defaultCurrency,
      temporalAddition: "0",
      temporalRemoval: "0",
      dueDate: null
    });
    setEditInstance(null);
    setSelectedFeatures([]);
    setActiveTab(0);
  }, [open, pricingInstanceId, reset, currencyOptions, onError]);

  useEffect(() => {
    if (!open || pricingInstanceId) return;
    if (!idBusinessUnit) {
      setAllUsers([]);
      setSelectedUserIds([]);
      setCanCreateTemporal(true);
      return;
    }
    const isTemporal = temporalAddition === "1" || temporalRemoval === "1";
    if (isTemporal && idBillingPeriod && packageRow?.id) {
      callApiAsync(
        () =>
          getUsersForNewPricingInstance({
            idBillingPeriod,
            idPackage: packageRow.id,
            idBusinessUnit,
            temporalAddition: temporalAddition === "1",
            temporalRemoval: temporalRemoval === "1"
          }),
        (data) => {
          setCanCreateTemporal(data?.canCreate !== false);
          setAllUsers(Array.isArray(data?.allUsers) ? data.allUsers : []);
          setSelectedUserIds([]);
        }
      );
    } else {
      setCanCreateTemporal(true);
      callApiAsync(
        () => getUsersByBusinessUnit(idBusinessUnit),
        (data) => {
          setAllUsers(Array.isArray(data) ? data : []);
          setSelectedUserIds([]);
        }
      );
    }
  }, [open, pricingInstanceId, idBusinessUnit, idBillingPeriod, packageRow?.id, temporalAddition, temporalRemoval]);

  useEffect(() => {
    if ((temporalAddition === "1" || temporalRemoval === "1") && open) {
      const current = getValues("dueDate");
      if (!current || !dayjs(current).isValid()) {
        setValue("dueDate", lastDayOfCurrentMonth());
      }
    }
  }, [temporalAddition, temporalRemoval, open, setValue, getValues]);

  const toggleUser = (user) => {
    const userId = user?.id ?? user?.Id;
    if (!userId) return;
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const toggleAllUsers = (checked) => {
    setSelectedUserIds(checked ? allUsers.map((u) => u.id ?? u.Id) : []);
  };

  const selectedRowsForTable = useMemo(
    () => allUsers.filter((u) => selectedUserIds.includes(u.id ?? u.Id)),
    [allUsers, selectedUserIds]
  );

  const userTableColumns = useMemo(
    () => [
      textColumn("lastName", "Apellido", 120),
      textColumn("name", "Nombre", 120),
      textColumn("email", "Correo", 200),
      textColumn("position", "Cargo", 140),
      {
        id: "chargeable",
        header: "Facturable",
        size: 110,
        accessorFn: (row) => row.chargeable ?? row.Chargeable,
        Cell: ({ row }) => {
          const v = row.original.chargeable ?? row.original.Chargeable;
          return v === true ? "Sí" : "No";
        }
      },
      {
        id: "licenseStatus",
        header: "Licencia",
        size: 130,
        Cell: ({ row }) => {
          const status = row.original.licenseStatus ?? row.original.LicenseStatus ?? 1;
          return (
            <StatusPill backgroundColor={licensePillColor(status)} textColor="#000">
              {licenseLabel(status)}
            </StatusPill>
          );
        }
      }
    ],
    []
  );

  const handleRemoveFeature = (row) => {
    setSelectedFeatures((prev) =>
      prev.filter((feature) => feature.id !== row.id)
    );
  };

  const featureActionBtn = ({ row }) => (
    <ButtonComponent
      onClick={() => handleRemoveFeature(row.original)}
      variant="contained"
      backgroundColor="#fff0"
    >
      <img className="btn-icon" src={icons.red_delete} alt="Eliminar feature" />
    </ButtonComponent>
  );

  const featureTableColumns = useMemo(
    () => [
      textColumn("productName", "Producto", 160),
      textColumn("name", "Feature", 200),
      actionsColumn(featureActionBtn, 50)
    ],
    []
  );

  const onSubmit = (formData) => {
    if (formData.temporalAddition === "1" && formData.temporalRemoval === "1") {
      onError(NOT_BOTH_TEMPORAL_MSG);
      return;
    }
    if (isEditMode) {
      if (!editInstance) return;
    } else {
      if (!packageRow?.id || !idBillingPeriod) {
        onError("Faltan periodo o paquete seleccionado.");
        return;
      }
      if (!canCreateTemporal && (formData.temporalAddition === "1" || formData.temporalRemoval === "1")) {
        onError(
          formData.temporalAddition === "1"
            ? "No se puede crear una adición temporal sin un paquete principal para este periodo y paquete."
            : "No se puede crear una remoción temporal sin un paquete principal para este periodo y paquete."
        );
        return;
      }
    }

    const dueDateValue = formData.dueDate
      ? (dayjs.isDayjs(formData.dueDate)
          ? formData.dueDate
          : dayjs(formData.dueDate)
        ).format("YYYY-MM-DDTHH:mm:ss")
      : null;

    const currencyValue = formData.currencyId ? parseInt(formData.currencyId, 10) : 0;
    const payload = isEditMode
      ? {
          id: editInstance.id ?? editInstance.Id,
          idBillingPeriod: editInstance.idBillingPeriod ?? editInstance.IdBillingPeriod,
          idPackage: editInstance.idPackage ?? editInstance.IdPackage,
          individualPrice: showIndividualPrice ? parsePrice(formData.individualPrice) : 0,
          packageSize: showPackageSize ? parseInt(formData.packageSize, 10) || 0 : 0,
          packagePrice: showPackagePrice ? parsePrice(formData.packagePrice) : 0,
          maxMembersToCharge: showMaxMembers ? parseInt(formData.maxMembersToCharge, 10) || 0 : 0,
          tax: parseFloat(String(formData.tax || 0).replace(",", ".")) || 0,
          currency: currencyValue === 1 ? 1 : 0,
          temporalAddition: formData.temporalAddition === "1",
          temporalRemoval: formData.temporalRemoval === "1",
          dueDate: dueDateValue,
          selectedUsers: selectedUserIds,
          selectedFeatures: selectedFeatures.map((f) => f.id)
        }
      : {
          id: 0,
          idBillingPeriod,
          idPackage: packageRow.id,
          individualPrice: showIndividualPrice ? parsePrice(formData.individualPrice) : 0,
          packageSize: showPackageSize ? parseInt(formData.packageSize, 10) || 0 : 0,
          packagePrice: showPackagePrice ? parsePrice(formData.packagePrice) : 0,
          maxMembersToCharge: showMaxMembers ? parseInt(formData.maxMembersToCharge, 10) || 0 : 0,
          tax: parseFloat(String(formData.tax || 0).replace(",", ".")) || 0,
          currency: currencyValue === 1 ? 1 : 0,
          temporalAddition: formData.temporalAddition === "1",
          temporalRemoval: formData.temporalRemoval === "1",
      dueDate: dueDateValue,
      selectedUsers: selectedUserIds,
      selectedFeatures: selectedFeatures.map((f) => f.id)
    };

    payload.userEmail = userData?.email ?? "";

    callApiAsync(
      () => createUpdatePricingInstance(payload),
      (data) => {
        if (data && (data.error || data.detail)) {
          const msg = data.detail ? `${data.error || "Error"}: ${data.detail}` : (data.error || "Error al crear.");
          onError(msg);
          return;
        }
        onSuccess();
      },
      (err) => {
        const msg =
          (err && typeof err === "object" && err.error) ||
          (err?.response?.data?.error) ||
          err?.error ||
          err?.message ||
          (typeof err === "string" ? err : "Error al crear el elemento de facturación");
        const detail = err?.detail ?? err?.response?.data?.detail;
        onError(detail ? `${msg}: ${detail}` : String(msg));
      }
    );
  };

  return (
    <ModalComponent
      open={open}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      title={isEditMode ? "Editar elemento de facturación" : "Agregar paquete a facturar"}
      customWidth="950px"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        {packageRow && !isEditMode && (
          <p style={{ marginBottom: 16, fontWeight: 600 }}>
            Paquete: {packageRow.name}
          </p>
        )}
        {isEditMode && editInstance && (
          <p style={{ marginBottom: 16, fontWeight: 600 }}>
            Editando elemento de facturación (periodo y paquete fijos)
          </p>
        )}

        <Grid container spacing={2}>
          <Grid item xs={12}>
            <SelectInput
              name="tipoFacturacion"
              control={control}
              label="Tipo de facturación"
              options={TIPO_FACTURACION_OPTIONS}
            />
          </Grid>

          {showIndividualPrice && (
            <Grid item xs={12} md={6}>
              <MoneyTextInput
                name="individualPrice"
                control={control}
                label="Precio individual"
              />
            </Grid>
          )}

          {showPackageSize && (
            <Grid item xs={12} md={6}>
              <TextInput
                name="packageSize"
                control={control}
                label="Tamaño del paquete"
                type="number"
              />
            </Grid>
          )}

          {showPackagePrice && (
            <Grid item xs={12} md={6}>
              <MoneyTextInput
                name="packagePrice"
                control={control}
                label="Precio del paquete"
              />
            </Grid>
          )}

          {showMaxMembers && (
            <Grid item xs={12} md={6}>
              <TextInput
                name="maxMembersToCharge"
                control={control}
                label="Máximo de miembros a cobrar"
                type="number"
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextInput
                  name="tax"
                  control={control}
                  label="Impuesto (%)"
                  type="number"
                  rules={{ min: { value: 0, message: "El impuesto no puede ser negativo" }, max: { value: 100, message: "El impuesto no puede ser mayor a 100%" } }}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectInput
              name="currencyId"
              control={control}
              label="Moneda"
              options={currencyOptions}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <DatePickerInput
              name="dueDate"
              control={control}
              label="Fecha de vencimiento (opcional)"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormRadioButton
              name="temporalAddition"
              control={control}
              label="Adición temporal"
              options={[
                { label: "No", value: "0" },
                { label: "Sí", value: "1" }
              ]}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormRadioButton
              name="temporalRemoval"
              control={control}
              label="Remoción"
              options={[
                { label: "No", value: "0" },
                { label: "Sí", value: "1" }
              ]}
            />
          </Grid>

          {!canCreateTemporal && (temporalAddition === "1" || temporalRemoval === "1") && (
            <Grid item xs={12}>
              <p style={{ marginBottom: 8, color: "#c62828", fontWeight: 600 }}>
                {temporalAddition === "1"
                  ? "No se puede crear una adición temporal: debe existir primero el elemento de facturación no temporal para este paquete y periodo."
                  : "No se puede crear una remoción temporal: debe existir primero el elemento de facturación no temporal para este paquete y periodo."}
              </p>
            </Grid>
          )}
          <Grid item xs={12}>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                textColor="primary"
                indicatorColor="primary"
              >
                <Tab label={`Usuarios (${selectedUserIds.length})`} />
                <Tab label={`Features (${selectedFeatures.length})`} />
              </Tabs>
            </Box>

            {activeTab === 0 && (
              <>
                {allUsers.length === 0 ? (
                  <p style={{ color: "#666" }}>No hay usuarios disponibles para este lab.</p>
                ) : (
                  <TableComponent
                    columns={userTableColumns}
                    data={allUsers}
                    onRowClick={toggleUser}
                    selectedRows={selectedRowsForTable}
                    multiSelect={true}
                    enableGlobalFilter={true}
                    enableColumnOrdering={false}
                    headerButtons={() => (
                      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                        <span style={{ marginRight: 8, fontWeight: 500 }}>
                          Usuarios seleccionados: {selectedUserIds.length}
                        </span>
                        <ButtonComponent
                          type="button"
                          variant="contained"
                          onClick={() => toggleAllUsers(true)}
                          textColor="#fff"
                        >
                          Seleccionar todos
                        </ButtonComponent>
                        <ButtonComponent
                          type="button"
                          variant="contained"
                          onClick={() => toggleAllUsers(false)}
                          textColor="#fff"
                          backgroundColor="#666"
                        >
                          Deseleccionar todos
                        </ButtonComponent>
                      </div>
                    )}
                  />
                )}
              </>
            )}

            {activeTab === 1 && (
              <>
                <TableComponent
                  columns={featureTableColumns}
                  data={selectedFeatures}
                  enableGlobalFilter={true}
                  headerButtons={() => (
                    <ButtonComponent
                      type="button"
                      onClick={() => setOpenFeatureModal(true)}
                      variant="contained"
                      backgroundColor="#F6C079"
                      textColor="#000000"
                    >
                      <div className="HContainer a-center">
                        <img className="btn-icon icon-margin-r-15" src={icons.plus} alt="icon" />
                        <p>Seleccionar features</p>
                      </div>
                    </ButtonComponent>
                  )}
                />
                <p style={{ marginTop: 8, color: "#666", fontSize: 14 }}>
                  Los features definen los permisos del lab en Intelapp (integración A&A).
                </p>
              </>
            )}
          </Grid>
        </Grid>

        <div className="HContainer j-center mt-20">
          <ButtonComponent
            type="button"
            onClick={onClose}
            backgroundColor="#fff"
            textColor="#666"
            width="120px"
            margin="0 10px 0 0"
          >
            Cancelar
          </ButtonComponent>
          <ButtonComponent
            type="submit"
            backgroundColor="#F6C079"
            textColor="#000000"
            width="160px"
            disable={!canCreateTemporal && (temporalAddition === "1" || temporalRemoval === "1")}
          >
            {isEditMode ? "Guardar" : "Agregar a facturar"}
          </ButtonComponent>
        </div>
      </form>

      <SelectFeatureModal
        open={openFeatureModal}
        onClose={() => setOpenFeatureModal(false)}
        onSubmit={(features) => {
          setSelectedFeatures(features);
          setOpenFeatureModal(false);
        }}
        selectedFeatures={selectedFeatures}
      />
    </ModalComponent>
  );
};

export default AddPackageToBillModal;
